<template>
    <div class="program-component">
        <card title="Program Record Maintenance">
            <SearchComponent v-model="program" :disabled="loadingProgram" label="Program"
                :paramsInGetById="{ includeDrugs: true, includePrescribers: true, includeFlags: true, includeAuditConfigurations: true }"
                search-u-r-l="/Program" prepopulate clearable @input="onProgramUpdate" />
            <hr />
            <div v-show="loadingProgram" class="text-center my-4">
                <b-spinner label="Loading program..."></b-spinner>
                <p class="mt-2">Loading program...</p>
            </div>
            <form v-show="!loadingProgram">
                <b-form-row>
                    <b-col>
                        <fg-input v-model="program.name" :disabled="disableFields" label="Name" required>
                        </fg-input>
                    </b-col>
                    <b-col>
                        <label>
                            Abbreviation
                            <span>*</span>
                            <help-popup placement="top">
                                Short identifier used throughout the system for this program (e.g., 'ACME', 'XYZ'). Displayed in dropdowns and reports.
                            </help-popup>
                        </label>
                        <fg-input v-model="program.abbreviation" :disabled="disableFields" required>
                        </fg-input>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col>
                        <label>
                            Memo
                            <help-popup placement="top">
                                Internal notes about this program. Not visible to patients or prescribers.
                            </help-popup>
                        </label>
                        <fg-input>
                            <textarea v-model="program.memo" :disabled="disableFields" class="form-control textarea-autosize">
                            </textarea>
                        </fg-input>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col>
                        <checkbox v-model="program.active" :disabled=disableFields inline>
                            Active
                            <help-popup placement="top">
                                Controls whether this program is available for prescriptions, patients, etc. Inactive programs are hidden from selection lists.
                            </help-popup>
                        </checkbox>
                        <checkbox v-model="program.forceMemo" :disabled=disableFields inline>
                            Force Memo
                        </checkbox>
                        <checkbox v-model="program.autoprocess" :disabled=disableFields inline>
                            Autoprocess
                            <help-popup placement="top">
                                When enabled, eligible prescriptions automatically advance through processing stages without manual intervention. Configure drug-specific settings in the Auto Process tab.
                            </help-popup>
                        </checkbox>

                    </b-col>
                </b-form-row>

                <b-tabs pills content-class="mt-3" class="mt-3" :value="activeTabIndex" @input="onActiveTabChanged">
                    <b-tab title="Features">
                        <!-- Functionality Flags -->
                        <div class="feature-flags-grid">
                            <div v-for="group in featureFlagGroups" :key="group.title" class="feature-group">
                                <strong>{{ group.title }}</strong>
                                <div>
                                    <div v-for="f in group.flags" :key="f.id">
                                        <checkbox v-model="f.value" :disabled="disableFields" inline>
                                            {{ f.name }}
                                            <help-popup v-if="f.description" placement="top">{{ f.description }}</help-popup>
                                        </checkbox>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </b-tab>
                    <b-tab title="Operations">
                        <div class="operations-grid">
                            <div>
                                <div class="mb-2">
                                    <label>
                                        Delivery
                                        <help-popup placement="top">
                                            Default shipping method for this program (e.g., Mail, Local Delivery, Pickup).
                                            <br/><br/>
                                            <strong>Order Creation Priority:</strong> The actual delivery method used is determined by the highest priority available option:
                                            <ol style="margin: 5px 0; padding-left: 20px;">
                                                <li>Prescription-level delivery code (if set)</li>
                                                <li>Drug-specific delivery code in Clinical tab (if set)</li>
                                                <li>This program delivery method (if set)</li>
                                                <li>Patient's default delivery preference (if set)</li>
                                                <li>CII controlled substances default to Two Day shipping</li>
                                            </ol>
                                            <em>Higher priority methods (Will Call, Overnight, Two Day) override lower priority methods (Standard, Delivery).</em>
                                        </help-popup>
                                    </label>
                                    <EnumSelector v-model="program.deliveryMethod" :clearable="true"
                                        :disabled="disableFields" :enum-codes="deliveryCodes"
                                        :display-names="deliveryCodeDisplayNames"
                                        :elements-to-discard="hiddenDeliveryCodes" />
                                    <div v-if="noCarriersEnabled" class="text-warning"
                                        style="font-size: 0.85em; margin-top: 4px;">
                                        Program carriers don't overlap with the tenant's allowed carriers.
                                        Only Will Call and Delivery options will work.
                                    </div>
                                </div>
                                <div>
                                    <label>
                                        Carriers
                                        <help-popup placement="top">
                                            Allowed shipping carriers for this program. If none are selected, all carriers are allowed.
                                        </help-popup>
                                    </label>
                                    <multiselect v-model="program.carriers" group-label="label" group-values="carriers"
                                        :disabled="disableFields" :options="carrierOptions" :close-on-select="false"
                                        multiple group-select />
                                </div>
                            </div>
                            <div>
                                <div class="mb-3">
                                    <label>
                                        Assigned Teams
                                        <help-popup placement="top">
                                            Teams with access to process prescriptions for this program. If no teams are configured, all users can access the program.
                                        </help-popup>
                                    </label>
                                    <multiselect v-model="program.teams" track-by="id" label="name"
                                        :disabled="disableFields" :options="teamOptions" :close-on-select="false"
                                        multiple />
                                </div>
                                <div v-if="hasInventoryLocations" class="mb-2">
                                    <label>
                                        Default Inventory Filling Location
                                        <help-popup placement="top">
                                            Primary location used for filling prescriptions when multiple inventory locations exist.
                                        </help-popup>
                                    </label>
                                    <SearchComponent :id="program.defaultFillingLocation?.id"
                                        v-model="program.defaultFillingLocation" :disabled="disableFields"
                                        search-u-r-l="/inventory/inventory-locations" :prepopulate="true"
                                        clearable />
                                </div>
                                <div id="exclusive-transfer-row">
                                    <label>
                                        Exclusive Transfer Pharmacies
                                        <help-popup placement="top">
                                            When configured, prescriptions can only be transferred to/from these specific pharmacies. Leave empty to allow transfers to any pharmacy. <hr><br> Note: This also affects the available options for patients in the patient app.
                                        </help-popup>
                                    </label>
                                    <multiselect v-model="program.transferToPharmacies"
                                        :options="exclusiveTransferPharmacyOptions" :multiple="true" label="name"
                                        track-by="id" :loading="loadingExclusiveTransferPharmacyOptions"
                                        @search-change="exclusiveTransferPharmacySearch" :disabled="disableFields">
                                        <template #noOptions>Start typing to search for a pharmacy.</template>
                                        <template #noResult>No pharmacies match your search.</template>
                                    </multiselect>
                                </div>
                            </div>
                        </div>
                    </b-tab>
                    <b-tab title="Financial & Audits">
                        <b-form-row>
                            <b-col>
                                <label>
                                    Price Schedule
                                    <help-popup placement="top">
                                        Pricing structure applied to prescriptions in this program. Determines base pricing, markup, and dispensing fees when a price schedule is used as a cash out when billing a prescription.
                                    </help-popup>
                                </label>
                                <fg-input>
                                    <v-select v-model="selectedPriceSchedule" :disabled="disableFields"
                                        :options='priceSchedules' class="style-chooser" label='name' />
                                </fg-input>
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col>
                                <label>
                                    Audit Chance
                                    <HelpPopup markdown="Static number as a percentage value. e.g., 100 = 100% - Random chance within this threshold will create an order audit record that must be completed prior to dispensing in the Order Audit Queue." />
                                </label>
                                <fg-input id="audit-chance-input"
                                    v-model.number="orderAuditConfiguration.percentageChance" min=0 max=100
                                    :error="auditChanceError" :disabled="disableFields" type="number" />
                            </b-col>
                            <b-col v-for="field in programAuditFields" :key="field.id">
                                <label>
                                    {{ field.label }}
                                    <HelpPopup :markdown="field.helpText" />
                                </label>
                                <fg-input :id="field.id" v-model.number="orderAuditConfiguration[field.prop]"
                                    :disabled="disableFields" type="number" />
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col cols="4">
                                <label>
                                    Billing Review Sample Rate (%)
                                    <HelpPopup markdown="Percentage of **auto-adjudicated** fills in this program sampled into the Billing Review queue, even when the claim outcome matched what was scheduled. e.g., 5 = 5%. Separate from **Audit Chance** above, which belongs to Order Audit. The effective rate is the higher of the store's and the program's value; leave blank to sample nothing at the program level. Requires the tenant-level **Enable Auto-Adjudication** configuration to be enabled." />
                                </label>
                                <fg-input id="auto-adjudication-audit-chance-input"
                                    v-model.number="program.autoAdjudicationAuditChance" min=0 max=100
                                    :error="autoAdjudicationAuditChanceError" :disabled="disableFields" type="number"
                                    placeholder="Not set" />
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col cols="4">
                                <label>
                                    Autoship Copay Max
                                    <HelpPopup placement="top">
                                        Maximum patient copay amount allowed for autoship order creation. When set, prescriptions with a patient pay amount at or below this value will be eligible for autoship. Leave blank to only autoship prescriptions with $0 copay (default behavior).
                                    </HelpPopup>
                                </label>
                                <fg-input v-model.number="program.autoshipCopayMax" :disabled="disableFields"
                                    type="number" min="0" step="0.01" placeholder="Default: $0.00" />
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col cols="6">
                                <b-form-group label="Patient App Post-Delivery Survey Question Set">
                                    <b-form-select :value="program.patientAppDeliverySurveyQuestionSetId"
                                        @change="onPatientAppDeliverySurveyQuestionSetChange"
                                        :options="patientAppDeliverySurveyQuestionSetOptions" :disabled="disableFields" />
                                    <small class="form-text text-muted">
                                        Question set rendered to the patient after they confirm a delivery in the Patient App. This program-level override takes precedence over Store and Tenant defaults (in default precedence mode).
                                    </small>
                                </b-form-group>
                            </b-col>
                        </b-form-row>
                    </b-tab>
                    <b-tab title="Contacts">
                        <div class="contacts-grid">
                            <div v-for="contact in contactForms" :key="contact.type">
                                <label><strong>{{ contact.title }}</strong>
                                    <HelpPopup placement="top">{{ contact.helpText }}</HelpPopup>
                                </label>
                                <fg-input v-model="contact.data.firstName" :disabled="disableFields" label="First Name" />
                                <fg-input v-model="contact.data.lastName" :disabled="disableFields" label="Last Name" />
                                <fg-input v-model="contact.data.phone" :disabled="disableFields" label="Phone" />
                                <fg-input v-model="contact.data.emailAddress" :disabled="disableFields" label="Email Address" />
                            </div>
                        </div>
                        <hr/>
                        <div class="contacts-grid">
                            <div class="mb-2">
                                <label>
                                    ApiKey
                                    <help-popup placement="top">
                                        Authentication key for external systems to integrate with this program via API. Keep this secure.
                                    </help-popup>
                                </label>
                                <fg-input v-model="program.apiKey" :disabled="disableFields"/>
                            </div>
                            <div></div>
                        </div>
                    </b-tab>
                    <b-tab title="Tasks">
                        <b-row cols="1">
                            <b-col class="mb-2">
                                <b-form-group>
                                    <label>
                                        Tasks SLA Business Days
                                        <help-popup placement="top">
                                            Service Level Agreement: number of business days before tasks for this program are considered overdue by default. Can be overriden on templates/ad-hoc tasks. Overdue tasks will send a notification to the assigned user/team.
                                        </help-popup>
                                    </label>
                                    <b-input v-model="program.userTaskSLADays" type="number" :disabled="disableFields"
                                        min="0"
                                        @blur="program.userTaskSLADays = !program.userTaskSLADays ? program.userTaskSLADays : Math.max(program.userTaskSLADays, 0)" />
                                </b-form-group>
                            </b-col>
                            <b-col class="mb-2">
                                <b-form-group>
                                    <label>
                                        Tasks Default Team
                                        <help-popup placement="top">
                                            Team automatically assigned to tasks created for this program if not overriden by a task template or via API.
                                        </help-popup>
                                    </label>
                                    <SearchComponent v-model="tasksDefaultTeam"
                                        search-u-r-l="/user/teams" :prepopulate="true" @input="taskTeamSelected"
                                        :disabled="disableFields" clearable :hide-label="true" />
                                </b-form-group>
                            </b-col>
                            <b-col class="mb-2">
                                <b-form-group>
                                    <label>
                                        Tasks Webhook Endpoint
                                        <help-popup placement="top">
                                            HTTP endpoint URL to receive task notifications via webhook when tasks are created, updated, or completed.
                                        </help-popup>
                                    </label>
                                    <b-input v-model="program.userTaskWebhookEndpoint" maxlength="150"
                                        :disabled="disableFields" />
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-tab>
                    <b-tab title="Clinical">
                        <b-row>
                            <!-- Prescribers Selection -->
                            <b-col>
                                <b-row>
                                    <b-col>
                                        <label>
                                            Doctors in Program
                                            <help-popup placement="top">
                                                Prescribers associated with this program. This association serves two purposes:
                                                <br/><br/>
                                                <strong>1. Automatic E-Script Routing:</strong> When an e-script arrives from a prescriber in this program, the prescription is automatically assigned to this program based on the prescriber's NPI.
                                                <br/><br/>
                                                <strong>2. Organizational Filtering:</strong> Helps filter and display prescribers related to the program in the user interface.
                                                <br/><br/>
                                                <em>Note: This does not restrict which prescribers can write prescriptions for patients in the program when created manually.</em>
                                            </help-popup>
                                        </label>
                                        <SearchComponent v-model="selectedDoctor" :disabled="disableFields"
                                            :search-u-r-l="`/Prescriber`" clearable
                                            @input="addDoctor" />
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col>
                                        <EntitySelectionList :list="prescribers" @remove-entity="removePrescriber" />
                                    </b-col>
                                </b-row>
                            </b-col>

                            <!-- Drugs Selection -->
                            <b-col>
                                <b-row>
                                    <b-col>
                                        <label>
                                            Drugs in Program
                                            <help-popup placement="top">
                                                Medications available in this program. Configure audits, delivery, and autoselect options for each drug.
                                            </help-popup>
                                        </label>
                                        <SearchComponent v-model="selectedDrug" :disabled="disableFields"
                                            search-u-r-l="/Drug" clearable @input="addDrug" />
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col>
                                        <EntitySelectionList :list="drugs" @remove-entity="removeDrug"
                                            :custom-id="getDrugId">
                                            <template v-slot:inputs='props'>
                                                <b-col cols="4">
                                                    <EnumSelector v-model="props.item.deliveryCode" label="Delivery"
                                                        :enum-codes="deliveryCodes" :disabled="disableFields"
                                                        :display-names="deliveryCodeDisplayNames"
                                                        :elements-to-discard="hiddenDeliveryCodes"
                                                        :clearable="true">
                                                    </EnumSelector>
                                                </b-col>
                                                <b-col cols="2">
                                                    <b-form-group>
                                                        <label>
                                                            Autoselect
                                                            <help-popup placement="top">
                                                                When enabled, this drug is automatically selected when creating prescriptions for this program. Only one drug per program can have autoselect enabled.
                                                            </help-popup>
                                                        </label>
                                                        <b-checkbox v-if="!disableAutoselect(props.item)"
                                                            v-model="props.item.autoselect" size="lg"></b-checkbox>
                                                        <span v-else>
                                                            <b>Already set</b> on {{
                                                                getAutoselectProgramName(props.item) }}
                                                        </span>
                                                    </b-form-group>
                                                </b-col>
                                                <b-col cols="3">
                                                    <label>
                                                        Disable Autorefill
                                                        <help-popup placement="top">
                                                            Prevents automatic refill subscription creation for this drug, even if the program has autorefill enabled.
                                                        </help-popup>
                                                    </label>
                                                    <br>
                                                    <Checkbox v-model="props.item.disableAutorefill" />
                                                </b-col>
                                                <b-col cols='3'>
                                                    <b-form-group>
                                                        <label>
                                                            Awaits Prior Auth
                                                            <help-popup placement="top">
                                                                When enabled, e-commerce orders for prescriptions will fall into "Awaiting Prior Authorization" status for 5 days or until a PA is closed with an override.
                                                            </help-popup>
                                                        </label>
                                                        <b-checkbox v-model="props.item.awaitsPriorAuth" size="lg" />
                                                    </b-form-group>
                                                </b-col>
                                                <b-col v-for="field in drugAuditFields" :key="field.prop" cols="4">
                                                    <b-form-group>
                                                        <label>
                                                            {{ field.label }}
                                                            <help-popup placement="top">{{ field.helpText }}</help-popup>
                                                        </label>
                                                        <b-form-input v-model="props.item[field.prop]" number type="number" />
                                                    </b-form-group>
                                                </b-col>
                                            </template>
                                        </EntitySelectionList>
                                    </b-col>
                                </b-row>
                            </b-col>
                        </b-row>
                    </b-tab>
                    <b-tab title="Timing">
                        <div class="timing-grid">
                            <div>
                                <strong>
                                    Doctor Follow Up Next Touch Days
                                    <help-popup placement="top">
                                        Automated schedule for physician follow-up reminders. Configure number of days between follow-up attempts.
                                    </help-popup>
                                </strong>
                                <DoctorFollowUpNextTouchField :value="program.doctorFollowUpNextTouchDays"
                                    @input="program.doctorFollowUpNextTouchDays = $event" />
                            </div>
                            <div>
                                <strong>
                                    Prior Authorization Next Touch Days
                                    <help-popup placement="top">
                                        Automated schedule for prior authorization follow-up. Configure number of days between status check attempts.
                                    </help-popup>
                                </strong>
                                <PriorAuthNextTouchField :value="program.priorAuthNextTouchDays"
                                    @input="program.priorAuthNextTouchDays = $event" />
                            </div>
                            <div>
                                <strong>PAP Next Touch Days</strong>
                                <PAPNextTouchField :value="program.papNextTouchDays"
                                    @input="program.papNextTouchDays = $event" />
                            </div>
                        </div>

                        <div class="mt-3">
                            <strong>
                                Awaiting Insurance Text Hours
                                <HelpPopup># of Hours Between texts</HelpPopup>
                            </strong>

                            <b-input v-model="program.awaitingInsuranceHours" type="number" min="0"
                                :disabled="disableFields"
                                @blur="program.awaitingInsuranceHours = !program.awaitingInsuranceHours ? undefined : Math.round(Math.max(program.awaitingInsuranceHours, 0))" />
                        </div>

                        <div class="mt-3">
                            <strong>
                                Doctor Follow Up Max Attempts
                                <HelpPopup>Overrides the tenant-level max attempts before a follow up item is automatically closed. Leave empty or 0 to use the tenant default. Applies only to follow ups raised from a prescription (Refill Authorization, Rx Clarification) — New Rx Request and Alternate Rx follow ups always use the tenant value.</HelpPopup>
                            </strong>

                            <b-input v-model="program.doctorFollowUpMaxAttempts" type="number" min="0"
                                :disabled="disableFields"
                                @blur="program.doctorFollowUpMaxAttempts = !program.doctorFollowUpMaxAttempts ? undefined : Math.round(Math.max(program.doctorFollowUpMaxAttempts, 0))" />
                        </div>
                    </b-tab>
                    <b-tab title="Text Messages">
                        <TextMessageTemplatesField v-model="program.textMessageTemplates" :disabled="disableFields"
                            :hide-templates="['passwordReset', 'awaitingInsurance', 'outOfNetwork', 'pharmacyInfo', 'transfer', 'rxRequestDenied']" />
                    </b-tab>
                    <b-tab title="Templates">
                        <!-- CUSTOM PATIENT EDUCATION TEMPLATE  -->
                        <label for=""><strong>Custom Patient Education Template</strong>
                            <HelpPopup popover-custom-class="text-message-template">
                                <div>
                                    <h6>Available Tokens <small class="text-muted">Click to copy to clipboard</small>
                                    </h6>
                                    <div class="tokens">
                                        <code v-for="tkn in availableTokens" :key="tkn"
                                            @click="copyText('\{\{' + tkn + '\}\}')">{{ "\{\{" +
                                                tkn + "\}\}" }}</code>
                                    </div>
                                </div>
                            </HelpPopup>
                        </label>
                        <div class="template-info mb-3">
                            <div>
                                <span v-if="customTemplateFile && customTemplateFile.size > 0" class="badge badge-info">
                                    {{ customTemplateFile.name }} (pending save)
                                </span>
                                <span v-else-if="program && program.customTemplateFileId" class="badge badge-primary">
                                    Custom Template Uploaded
                                    <i class="ml-1">
                                        ({{ program.customTemplateUpdated | formatDate('MM-DD-YYYY, h:mm:ss a') }})
                                    </i>
                                </span>
                                <span v-else class="badge badge-secondary">Using Default</span>
                            </div>
                            <div class="template-actions mt-2">
                                <b-button
                                    v-if="program && program.customTemplateFileId"
                                    variant="outline-danger"
                                    size="sm"
                                    :disabled="disableFields || removingTemplate"
                                    class="mr-2 mb-2"
                                    @click="removeCustomTemplate">
                                    <i v-if="removingTemplate" class="fas fa-spinner fa-spin"></i>
                                    <i v-else class="fas fa-trash"></i>
                                    Remove Override
                                </b-button>
                                <b-button
                                    variant="primary"
                                    size="sm"
                                    :disabled="disableFields || uploadingTemplate"
                                    class="mr-2 mb-2"
                                    @click="$refs.templateFileInput.click()">
                                    <i v-if="uploadingTemplate" class="fas fa-spinner fa-spin"></i>
                                    <i v-else class="fas fa-upload"></i>
                                    {{ program && program.customTemplateFileId ? 'Replace Template' : 'Upload Template' }}
                                </b-button>
                                <b-button
                                    v-if="program && program.customTemplateFileId"
                                    variant="primary"
                                    size="sm"
                                    class="mr-2 mb-2"
                                    @click="downloadCustomTemplate">
                                    <i class="fas fa-download"></i>
                                    Download Custom
                                </b-button>
                                <b-button
                                    variant="primary"
                                    size="sm"
                                    class="mb-2"
                                    @click="downloadDefaultTemplate">
                                    <i class="fas fa-download"></i>
                                    Download Default
                                </b-button>
                            </div>
                        </div>

                        <input
                            ref="templateFileInput"
                            type="file"
                            accept=".docx"
                            style="display: none"
                            @change="handleTemplateFileSelection" />

                        <div v-if="templateUploadProgress > 0 && templateUploadProgress < 100" class="mb-3">
                            <b-progress :value="templateUploadProgress" :max="100" animated>
                                <span>{{ Math.round(templateUploadProgress) }}%</span>
                            </b-progress>
                        </div>

                        <div v-if="templateErrorMessage" class="alert alert-danger">
                            {{ templateErrorMessage }}
                        </div>

                        <b-modal
                            ref="removeTemplateModal"
                            title="Remove Template Override"
                            ok-title="Remove"
                            ok-variant="danger"
                            cancel-title="Cancel"
                            @ok="confirmRemoveTemplate">
                            <p>Are you sure you want to remove the custom patient education template?</p>
                            <p class="text-muted">The system will revert to using the default template.</p>
                        </b-modal>

                        <hr>

                        <label>
                            <strong>Email Templates</strong>
                            <help-popup placement="top">
                                Customize email messages sent to patients for this program. Override global templates with program-specific messaging.
                            </help-popup>
                        </label>
                        <EmailTemplatesField v-model="program.emailTemplates" :disabled="disableFields" />

                        <hr />

                        <label>
                            <strong>Fax Cover Page</strong>
                            <help-popup placement="top">
                                Override the tenant-level fax cover page setting for faxes sent for prescriptions in this program. Leave on "Use tenant default" to inherit the global setting.
                            </help-popup>
                        </label>
                        <b-form-select v-model="program.faxCoverPage" :options="faxCoverPageOptions"
                            :disabled="disableFields" />

                        <hr />

                        <div v-if="!!program.id" class="fax-templates-section">
                            <FaxTemplateField v-model="program.faxTemplateOverrides" />
                        </div>
                    </b-tab>
                    <b-tab title="Auto Process">
                        <ProgramAutoProcessTab :program="program"
                            @update:autoprocessAllDrugs="program.autoprocessAllDrugs = $event"
                            @update:autoprocessDefaults="program.autoprocessDefaults = $event" />
                    </b-tab>
                    <b-tab title="Status Display" lazy>
                        <ProgramStatusDisplayTab :program="program" />
                    </b-tab>
                </b-tabs>
            </form>

            <NewClearSaveButtons :disable="disableFields" :disable-save="!isFormValid"
                :titles="buttonTitles" @new-clicked="createProgram"
                @save-clicked="saveProgram" sticky />

        </card>

        <b-modal id="programPatientAppDeliverySurveyQuestionSetsModal" hide-footer no-fade
            title="Patient App Post-Delivery Survey Question Sets" size="lg"
            @hidden="reloadPatientAppDeliverySurveyQuestionSetsAfterModalClose">
            <TaskQuestionSets :entity-type="EntityType.PatientAppDeliverySurvey" />
        </b-modal>

        <FedExDisclaimer />
    </div>
</template>

<script lang="ts">
import axios, { CancelTokenSource } from 'axios';
import { BModal } from 'bootstrap-vue';
import { Component, Mixins, Watch } from 'vue-property-decorator';
import DisableAutocompleteMixin from '@/mixins/DisableAutocompleteMixin';

import TextMessageTemplatesField from "@/components/Configuration/TextMessageTemplatesField.vue";
import SearchComponent from '@/components/SearchComponent.vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import EntitySelectionList from "@/components/EntitySelectionList.vue";
import NewClearSaveButtons from '@/components/NewClearSaveButtons.vue';
import EnumSelector from '@/components/EnumSelector.vue';
import Multiselect from "vue-multiselect";
import { Program } from '@/models/Program';
import { Prescriber } from '@/models/Prescriber';
import { Drug, DrugProgram } from '@/models/Drug/Drug';
import { Contact, ContactType } from "@/models/Contact";
import { FunctionalityFlag } from "@/models/FunctionalityFlag";
import { PriceSchedule } from "@/models/PriceSchedule";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { DeliveryCode, DeliveryCodeCarrierMap, DeliveryCodeDisplayNames } from '@/models/DeliveryCode';
import FedExDisclaimer from '@/components/Shipping/FedExDisclaimer.vue';
import { OrderAuditConfiguration } from "@/models/OrderAudit";
import { DrugProduct } from '@/models/Drug/DrugProduct';
import { EntityType } from "@/models/EntityType";
import { PharmacyRecord } from "@/models/Pharmacy";
import { Team } from '@/models/Team';
import { debounce } from 'throttle-debounce';
import Axios from 'axios';
import DoctorFollowUpNextTouchField from '@/components/Configuration/DoctorFollowUpNextTouchField.vue';
import PriorAuthNextTouchField from '@/components/Configuration/PriorAuthNextTouchField.vue';
import PAPNextTouchField from '@/components/Configuration/PAPNextTouchField.vue';
import HelpPopup from '@/components/HelpPopup.vue';
import EmailTemplatesField from "@/components/Configuration/EmailTemplatesField.vue";
import { ButtonTitles } from "@/components/ButtonTitles";
import FaxTemplateField from "@/components/Configuration/FaxTemplateField.vue";
import TaskQuestionSets from "@/components/Tasks/TaskQuestionSets.vue";
import ProgramAutoProcessTab from "@/pages/Program/ProgramAutoProcessTab.vue";
import ProgramStatusDisplayTab from "@/pages/Program/ProgramStatusDisplayTab.vue";

@Component({
    name: 'ProgramDetailsPage',
    components: {
        EmailTemplatesField,
        FedExDisclaimer,
        SearchComponent,
        Checkbox,
        NewClearSaveButtons,
        EntitySelectionList,
        EnumSelector,
        Multiselect,
        TextMessageTemplatesField,
        DoctorFollowUpNextTouchField,
        PriorAuthNextTouchField,
        PAPNextTouchField,
        HelpPopup,
        FaxTemplateField,
        TaskQuestionSets,
        ProgramAutoProcessTab,
        ProgramStatusDisplayTab,
    },
})
export default class ProgramDetailsPage extends Mixins(DisableAutocompleteMixin) {
    EntityType = EntityType;

    private program: Program = new Program();
    private businessContact: Contact = { type: ContactType.Business } as Contact;
    private programContact: Contact = { type: ContactType.Program } as Contact;
    private flags: FunctionalityFlag[] = [];
    private clearFlags: FunctionalityFlag[] = [];
    private priceSchedules: PriceSchedule[] = [];
    private selectedPriceSchedule: PriceSchedule = {} as PriceSchedule;
    private customTemplateFile: File = new File([], "");
    private uploadingTemplate: boolean = false;
    private removingTemplate: boolean = false;
    private templateUploadProgress: number = 0;
    private templateErrorMessage: string = '';
    protected deliveryCodes = DeliveryCode;
    protected deliveryCodeDisplayNames = DeliveryCodeDisplayNames;
    protected tenantAllowedCarriers: string[] = [];

    private get effectiveCarriers(): string[] | null {
        const programCarriers = (this.program?.carriers ?? []).map(c => c.toLowerCase());
        const tenantCarriers = (this.tenantAllowedCarriers ?? []).map(c => c.toLowerCase());
        if (programCarriers.length === 0 && tenantCarriers.length === 0) return null;
        if (programCarriers.length === 0) return tenantCarriers;
        if (tenantCarriers.length === 0) return programCarriers;
        return programCarriers.filter(c => tenantCarriers.includes(c));
    }

    protected get noCarriersEnabled(): boolean {
        const effective = this.effectiveCarriers;
        return effective !== null && effective.length === 0;
    }

    protected get hiddenDeliveryCodes(): DeliveryCode[] {
        const effective = this.effectiveCarriers;
        const noCarriers = this.noCarriersEnabled;
        return Object.values(DeliveryCode)
            .filter((v): v is DeliveryCode => typeof v === 'number')
            .filter(code => {
                const required = DeliveryCodeCarrierMap[code]?.requiredCarrier;
                if (required === "") return false;
                if (required === null) return noCarriers;
                return effective === null ? false : !effective.includes(required.toLowerCase());
            });
    }

    protected selectedDoctor: Prescriber = new Prescriber();
    private prescribers: Array<Prescriber> = new Array<Prescriber>();

    protected selectedDrug: DrugProduct = new Drug();
    private drugs: Array<DrugProgram> = new Array<DrugProgram>();
    private orderAuditConfiguration: OrderAuditConfiguration = new OrderAuditConfiguration();
    protected pharmacy: PharmacyRecord = new PharmacyRecord();

    protected tasksDefaultTeam: Team = new Team();

    protected buttonTitles: ButtonTitles = {
        save: "Save Changes",
        new: "New Program",
        clear: "",
        cancel: "",
    };

    protected availableTokens: string[] = ["PatientName", "PatientAddress", "DateOfBirth", "PatientGender", "CP",
        "PrescriberName", "Shipping", "FillDate", "DrugName", "Qty", "RxNbr",
        "RfNbr", "Rf", "TotalPrice", "Copay", "RfLeft", "ProgramName",
        "InventoryLocation", "NDC", "DescriptionHeader", "Description",
        "DescriptionFooter", "ContraindicationsHeader", "Contraindications",
        "ContraindicationsFooter", "AdministrationHeader", "Administration",
        "AdministrationFooter", "MissedDoseHeader", "MissedDose", "MissedDoseFooter",
        "MonitoringHeader", "Monitoring", "MonitoringFooter", "SideEffectsHeader",
        "SideEffects", "SideEffectsFooter", "StorageHeader", "Storage",
        "StorageFooter", "ConsignmentRx", "PharmacyInfo", "PharmacyPhone"];

    protected carrierOptions: { label: string, carriers: string[] }[] = [];
    protected teamOptions: Team[] = [];
    protected patientAppDeliverySurveyQuestionSets: Array<{ id: number; name: string }> = [];

    get patientAppDeliverySurveyQuestionSetOptions() {
        const sets = this.patientAppDeliverySurveyQuestionSets;
        if (sets.length === 0) {
            return [
                { value: null, text: '(Inherit from store/tenant)' },
                { value: '__manage__', text: '+ Create your first question set...' },
            ];
        }
        return [
            { value: null, text: '(Inherit from store/tenant)' },
            ...sets.map(qs => ({ value: qs.id, text: qs.name })),
            { value: '__manage__', text: '+ Create / Manage question sets...' },
        ];
    }

    onPatientAppDeliverySurveyQuestionSetChange(value: any) {
        if (value === '__manage__') {
            // Sentinel: open the manage modal; don't actually mutate the program field.
            this.$bvModal.show('programPatientAppDeliverySurveyQuestionSetsModal');
            return;
        }
        this.program.patientAppDeliverySurveyQuestionSetId = value;
    }

    async reloadPatientAppDeliverySurveyQuestionSetsAfterModalClose() {
        try {
            const resp = await axios.get<any[]>(
                `/api/Task/question-sets/${EntityType.PatientAppDeliverySurvey}`);
            this.patientAppDeliverySurveyQuestionSets = (resp.data ?? []).filter((qs: any) => qs.active !== false);
        } catch {
            // non-critical; the dropdown stays at the previously fetched state.
        }
    }

    protected exclusiveTransferPharmacyOptions: Array<PharmacyRecord> = [];
    protected loadingExclusiveTransferPharmacyOptions = false;

    $refs!: {
        templateFileInput: HTMLInputElement;
        removeTemplateModal: BModal;
    };

    private exclusiveTransferPharmacySearchCancelTokenSource: CancelTokenSource | null = null;

    exclusiveTransferPharmacySearch(searchQuery: string) {
        this.exclusiveTransferPharmacySearchCancelTokenSource?.cancel("New search started");
        this.doDebouncedExclusiveTransferPharmacySearch(searchQuery, this);
    }

    private doDebouncedExclusiveTransferPharmacySearch = debounce(500, async (searchQuery, context) => {
        if (!searchQuery?.trim()) {
            context.exclusiveTransferPharmacyOptions = [];
            return;
        }

        context.loadingExclusiveTransferPharmacyOptions = true;

        context.exclusiveTransferPharmacySearchCancelTokenSource = Axios.CancelToken.source();

        try {
            const resp = await Axios.get<PharmacyRecord[]>("/StoredPharmacy", {
                params: { searchTerm: searchQuery, showOnlyActive: true },
                cancelToken: context.exclusiveTransferPharmacySearchCancelTokenSource.token
            });
            context.exclusiveTransferPharmacyOptions = resp.data;
        } catch (err) {
            console.log(err);
        } finally {
            context.loadingExclusiveTransferPharmacyOptions = false;
        }
    })

    private newMode = false;
    private loadingProgram = false;

    // URL-hash-based tab persistence: program/2#autoprocess restores that tab on
    // reload and makes the link shareable. Order of slugs must match the order of
    // <b-tab> declarations in the template; adding a new tab requires appending its
    // slug here.
    private static readonly tabSlugs = [
        'features',
        'operations',
        'financial',
        'contacts',
        'tasks',
        'clinical',
        'timing',
        'text-messages',
        'templates',
        'autoprocess',
    ];
    private activeTabIndex = (() => {
        const slug = (window.location.hash || '').replace(/^#/, '').toLowerCase();
        const idx = ProgramDetailsPage.tabSlugs.indexOf(slug);
        return idx >= 0 ? idx : 0;
    })();

    private onActiveTabChanged(index: number) {
        this.activeTabIndex = index;
        const slug = ProgramDetailsPage.tabSlugs[index];
        if (!slug) return;
        // history.replaceState avoids polluting the back/forward stack with every
        // tab switch — the user reloading the page is the only consumer of this hash.
        const newUrl = `${window.location.pathname}${window.location.search}#${slug}`;
        history.replaceState(history.state, '', newUrl);
    }

    get routeProgramId(): number {
        let res = parseInt(this.$route.params.id);
        if (isNaN(res)) return 0;
        return res;
    }

    get disableFields(): boolean {
        return this.loadingProgram || (!(this.program && this.program.id) && !this.newMode);
    }

    get serverURL() {
        return axios.defaults.baseURL;
    }

    get isFormValid(): boolean {
        return !!(this.program?.name && this.program?.abbreviation);
    }

    get faxCoverPageOptions() {
        return [
            { value: null, text: "Use tenant default" },
            { value: true, text: "Enabled" },
            { value: false, text: "Disabled" },
        ];
    }

    get auditChanceError(): string {
        if (this.orderAuditConfiguration.percentageChance == undefined) return "";
        if (this.orderAuditConfiguration.percentageChance > 100) return "Must be 100 or less";
        if (this.orderAuditConfiguration.percentageChance < 0) return "Must be 0 or greater";
        return "";
    }

    /** Blank is a valid value here — it means "unset", which samples nothing. */
    get autoAdjudicationAuditChanceError(): string {
        const chance = this.program?.autoAdjudicationAuditChance;
        if (chance == undefined || chance.toString() === "") return "";
        if (chance > 100) return "Must be 100 or less";
        if (chance < 0) return "Must be 0 or greater";
        return "";
    }

    /** Flag category definitions with keywords for matching */
    private readonly flagCategories = [
        {
            title: 'Order Processing & Automation',
            keywords: ['autoprocess', 'autoship', 'autorefill', 'pv1', 'autoselect only', 'refills', 'transfer', 'partial fill']
        },
        {
            title: 'Payment & Insurance',
            keywords: ['collected by partner', 'copay', 'tracking text', 'insurance', 'pending billing', 'skip prior auth']
        },
        {
            title: 'Inventory & Compliance',
            keywords: ['inventory', 'lot number', 'serial number', 'partner fill', 'print queue', 'smart post', 'smartpost']
        },
        {
            title: 'Patient Notifications & Messaging',
            keywords: ['notification', 'text', 'refill request', 'refill messages', 'patientapp', 'contact', 'delivery']
        },
        {
            title: 'External Integrations',
            keywords: ['therigy']
        },
        {
            title: 'Other Features',
            keywords: [] // Catch-all for uncategorized flags
        }
    ];

    get filteredFeatureFlags() {
        return this.flags.filter(flag => flag.name.toLowerCase() !== 'commissionable');
    }

    get orderProcessingFlags() {
        return this.filteredFeatureFlags.filter(flag => {
            const name = flag.name.toLowerCase();
            return name.includes('autoprocess') ||
                name.includes('autoship') ||
                name.includes('autorefill') ||
                name.includes('medsync') ||
                name.includes('pv1') ||
                name.includes('autoselect only') ||
                name.includes('refills') ||
                name.includes('transfer');
        });
    }
    private readonly drugAuditFields = [
        {
            label: 'Minimum Margin',
            prop: 'minimumMargin',
            helpText: 'Drug-specific minimum profit margin override. Overrides program-level margin settings.'
        },
        {
            label: 'Maximum Margin',
            prop: 'maximumMargin',
            helpText: 'Drug-specific maximum profit margin override. Overrides program-level margin settings.'
        },
        {
            label: 'Maximum Copay',
            prop: 'maximumCopay',
            helpText: 'Drug-specific maximum copay override. Overrides program-level copay settings.'
        }
    ];

    private readonly programAuditFields = [
        {
            id: 'audit-min-margin',
            label: 'Minimum Margin',
            prop: 'minimumMargin',
            helpText: 'Dollar amount. e.g., 10 - Prescription margins below this threshold will create an order audit record that must be completed prior to dispensing in the Order Audit Queue.'
        },
        {
            id: 'audit-max-margin',
            label: 'Maximum Margin',
            prop: 'maximumMargin',
            helpText: 'Dollar amount. e.g., 10 - Prescription margins above this threshold will create an order audit record that must be completed prior to dispensing in the Order Audit Queue.'
        },
        {
            id: 'audit-max-copay',
            label: 'Maximum Copay',
            prop: 'maximumCopay',
            helpText: 'Dollar amount. e.g., 10 - A patient copay value above this threshold will create an order audit record that must be completed prior to dispensing in the Order Audit Queue.'
        }
    ];

    get contactForms() {
        return [
            {
                type: ContactType.Business,
                title: 'Business Contact',
                helpText: 'Primary business contact for this program. Informational purposes only.',
                data: this.businessContact
            },
            {
                type: ContactType.Program,
                title: 'Program Contact',
                helpText: 'Primary contact for program-specific questions. Informational purposes only.',
                data: this.programContact
            }
        ];
    }

    get featureFlagGroups(): Array<{ title: string; flags: FunctionalityFlag[] }> {
        const assignedFlagIds = new Set<number>();

        return this.flagCategories.map(category => {
            let flags: FunctionalityFlag[];

            if (category.keywords.length === 0) {
                // "Other Features" - flags not assigned to any category
                flags = this.filteredFeatureFlags.filter(flag => !assignedFlagIds.has(flag.id));
            } else {
                flags = this.filteredFeatureFlags.filter(flag => {
                    if (assignedFlagIds.has(flag.id)) return false;
                    const name = flag.name.toLowerCase();
                    return category.keywords.some(keyword => name.includes(keyword));
                });
            }

            // Track assigned flags
            flags.forEach(f => assignedFlagIds.add(f.id));

            return { title: category.title, flags };
        });
    }

    async created() {
        console.log(`%c Created ${this.$options.name}`, "color: green");

        // Fetch all initial data in parallel
        const [functionalitiesResult, priceSchedulesResult, teamsResult, carriersResult, surveyQuestionSetsResult, configResult] = await Promise.allSettled([
            axios.get(`/Program/${0}/functionalities`),
            axios.get(`/PriceSchedule`),
            axios.get<Team[]>('/api/User/teams'),
            axios.get<string[]>('/api/Shipping/available-carriers'),
            axios.get<any[]>(`/api/Task/question-sets/${EntityType.PatientAppDeliverySurvey}`),
            axios.get<{ allowedCarriers: string[] | null }>('/api/Configuration')
        ]);

        if (surveyQuestionSetsResult.status === 'fulfilled') {
            this.patientAppDeliverySurveyQuestionSets = (surveyQuestionSetsResult.value.data ?? [])
                .filter((qs: any) => qs.active !== false);
        }

        if (functionalitiesResult.status === 'fulfilled') {
            this.clearFlags = functionalitiesResult.value.data;
            this.flags = functionalitiesResult.value.data;
        } else {
            console.warn('Failed to load functionalities:', functionalitiesResult.reason);
        }

        if (priceSchedulesResult.status === 'fulfilled') {
            this.priceSchedules = priceSchedulesResult.value.data;
        } else {
            console.warn('Failed to load price schedules:', priceSchedulesResult.reason);
        }

        if (teamsResult.status === 'fulfilled') {
            this.teamOptions = teamsResult.value.data.map(t => new Team(t));
        } else {
            console.warn('Failed to load teams:', teamsResult.reason);
        }

        if (carriersResult.status === 'fulfilled' && carriersResult.value.data.length > 0) {
            this.carrierOptions = [{ label: "All Carriers", carriers: carriersResult.value.data }];
        } else {
            this.carrierOptions = [{ label: "All Carriers", carriers: ['UPS', 'USPS', 'FedEx'] }];
        }

        if (configResult.status === 'fulfilled') {
            this.tenantAllowedCarriers = configResult.value.data?.allowedCarriers ?? [];
        } else {
            console.warn('Failed to load tenant configuration:', configResult.reason);
        }

        if (this.routeProgramId) {
            this.fetchProgram(this.routeProgramId);
        }

        this.loadHasInventoryLocations();
    }

    taskTeamSelected() {
        this.program.userTaskDefaultTeamId = this.tasksDefaultTeam?.id;
    }

    createProgram() {
        this.program = new Program();
        this.program.active = true;
        this.newMode = true;
        this.flags = this.clearFlags;
    }

    getAutoselectProgramName(item: DrugProgram) {
        return item.autoselectProgram && item.autoselectProgram.abbreviation;
    }

    async saveProgram() {
        if (!this.program) {
            console.warn("We have no program to save, get a new one?");
            return;
        }

        const data = this.program;
        data.programContact = this.programContact;
        data.businessContact = this.businessContact;
        data.priceSchedule = this?.selectedPriceSchedule?.name ? this.selectedPriceSchedule : null;
        data.priceScheduleId = this.selectedPriceSchedule?.name ? this.selectedPriceSchedule.id : null;
        data.flags = this.flags;
        data.prescribers = this.prescribers;
        data.drugs = this.drugs;
        if (this.program.priceAdjustment?.toString() == "") {
            data.priceAdjustment = null;
        }
        // Clearing the input leaves "" behind rather than null, which will not bind to a nullable int.
        if (this.program.autoAdjudicationAuditChance?.toString() == "") {
            data.autoAdjudicationAuditChance = null;
        }
        const orderAuditConfigurations = this.program.orderAuditConfigurations;

        try {
            const response = await axios.post<Program>('api/program', data);
            this.program = new Program(response.data);

            // Upload custom template if there is any
            if (this.customTemplateFile.size > 0) {
                this.uploadingTemplate = true;
                this.templateUploadProgress = 0;
                this.templateErrorMessage = '';
                try {
                    const formData = new FormData();
                    formData.append('customTemplate', this.customTemplateFile);
                    const templateResponse = await axios.post<Program>(
                        `api/program/custom-template/${this.program.id}`,
                        formData,
                        {
                            headers: { 'Content-Type': 'multipart/form-data' },
                            onUploadProgress: (progressEvent) => {
                                if (progressEvent.total) {
                                    this.templateUploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                }
                            }
                        }
                    );
                    this.program = new Program(templateResponse.data);
                    if (this.program.customTemplateFileId > 0) {
                        this.$notification(NotificationOptions.successSaveNotificationPreset('Custom Template'));
                        this.customTemplateFile = new File([], "");
                    }
                } catch (err) {
                    this.templateErrorMessage = 'Failed to upload custom template.';
                    this.$notification(NotificationOptions.errorSaveNotificationPreset('Custom Template', err));
                    console.error(err, { err, response: err?.response });
                } finally {
                    this.uploadingTemplate = false;
                    this.templateUploadProgress = 0;
                }
            }

            this.orderAuditConfiguration.entityId = +this.program.id;
            this.orderAuditConfiguration.entityType = EntityType.Program;
            this.orderAuditConfiguration.sanitize();

            let orderAuditConfigs = [];
            for (const drug of this.drugs) {
                let orderAuditConfig = orderAuditConfigurations?.find(v => v.id == drug.orderAuditId);
                if (!orderAuditConfig) {
                    orderAuditConfig = new OrderAuditConfiguration();
                } else {
                    orderAuditConfig = new OrderAuditConfiguration(orderAuditConfig);
                }
                orderAuditConfig.entityId = +this.program.id;
                orderAuditConfig.entityType = EntityType.Program;
                orderAuditConfig.minimumMargin = drug.minimumMargin;
                orderAuditConfig.maximumMargin = drug.maximumMargin;
                orderAuditConfig.maximumCopay = drug.maximumCopay;
                orderAuditConfig.drugId = drug.packageID;
                orderAuditConfig.drugSource = drug.drugSource;
                orderAuditConfig.sanitize();

                orderAuditConfigs.push(orderAuditConfig);
            }
            orderAuditConfigs.push(this.orderAuditConfiguration);

            try {
                await axios.post(`/api/Program/orderAuditConfigurations`, orderAuditConfigs);
            } catch (err) {
                this.$notification(NotificationOptions.error(err));
            }

            this.$notification(NotificationOptions.notificationOptionsPreset(
                "Program Successfully Saved",
                NotificationOptions.NotificationTypes.success
            ));
        } catch (error) {
            console.error("Error on saving program", error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    @Watch('$route.params.id')
    onParamChanged(value: string, oldValue: string) {
        if (value != oldValue) {
            this.fetchProgram(Number(value));
        }
    }

    @Watch('program')
    onProgramChanged(value: Program | null, _oldValue: Program | null) {
        if (!value) return;

        if (value.id && value.id != 0 && value.id != this.routeProgramId) {
            this.$router.push({ name: 'ProgramDetails', params: { id: value.id.toString() } });
        }

        if (this.routeProgramId && value.id == 0) {
            this.$router.push({ name: 'Program' });
        }
    }

    async fetchProgram(programId: number) {
        if (!programId) {
            this.program = new Program();
            return;
        }

        this.loadingProgram = true;

        try {
            // Fetch program and contacts in parallel
            const [programResult, contactsResult] = await Promise.allSettled([
                axios.get<Program>(`api/Program/${programId}`, {
                    params: {
                        includeDrugs: true,
                        includePrescribers: true,
                        includeFlags: true,
                        includeAuditConfigurations: true,
                    },
                }),
                axios.get<Array<Contact>>(`/Program/${programId}/contacts`)
            ]);

            // Process contacts
            if (contactsResult.status === 'fulfilled') {
                this.applyContacts(contactsResult.value.data);
            } else {
                console.warn('Failed to load contacts:', contactsResult.reason);
                this.applyContacts([]);
            }

            // Process program
            if (programResult.status === 'fulfilled' && programResult.value.data?.id) {
                const program = new Program(programResult.value.data);
                this.program = program;
                this.applyProgramData(program);

                // Fetch task team if needed (dependent on program data)
                if (program.userTaskDefaultTeamId) {
                    try {
                        const teamResponse = await axios.get<Team>(`/api/user/teams/${program.userTaskDefaultTeamId}`);
                        this.tasksDefaultTeam = new Team(teamResponse.data);
                    } catch (error) {
                        console.warn('Failed to load task team:', error);
                    }
                } else {
                    this.tasksDefaultTeam = new Team();
                }
            } else if (programResult.status === 'rejected') {
                console.error('Error while getting program data.', programResult.reason);
            }

            this.resetSelections();
        } finally {
            this.loadingProgram = false;
        }
    }

    async onProgramUpdate(value: Program) {
        if (!value?.id) {
            this.resetForm();
            return;
        }

        // The b-search-component emits a lightweight Program record that does not include flags,
        // audit configs, drugs, or prescribers. Loading from those fields would write a default-
        // initialised flag set into the form, and a subsequent Save would erase the program's
        // configured flags (e.g. "Disable Welcome Text"). Defer to fetchProgram so the same
        // includeFlags/includeAuditConfigurations payload that the route-driven load uses populates
        // the form.
        await this.fetchProgram(value.id);
    }

    private resetForm() {
        this.program = new Program();
        this.newMode = false;
        this.businessContact = { type: ContactType.Business } as Contact;
        this.programContact = { type: ContactType.Program } as Contact;
        this.selectedPriceSchedule = {} as PriceSchedule;
        this.flags = this.clearFlags;
        this.selectedDoctor = new Prescriber();
        this.selectedDrug = new Drug();
        this.drugs = new Array<DrugProgram>();
        this.prescribers = new Array<Prescriber>();
        this.orderAuditConfiguration = new OrderAuditConfiguration();
        this.pharmacy = new PharmacyRecord();
        this.tasksDefaultTeam = new Team();
    }

    private applyContacts(contacts: Contact[]) {
        this.programContact = contacts.find(c => c.type == ContactType.Program) || { type: ContactType.Program } as Contact;
        this.businessContact = contacts.find(c => c.type == ContactType.Business) || { type: ContactType.Business } as Contact;
    }

    private applyProgramData(program: Program) {
        this.drugs = program.drugs?.map(dp => new DrugProgram(dp)) || [];
        this.prescribers = program.prescribers?.map(doc => Object.assign(new Prescriber(), doc)) || [];
        this.flags = program.flags || this.clearFlags;
        this.selectedPriceSchedule = this.priceSchedules.find(sm => sm.id == program?.priceSchedule?.id) as PriceSchedule;

        if (program.orderAuditConfigurations?.length) {
            const auditConfigurations = program.orderAuditConfigurations;
            this.orderAuditConfiguration = new OrderAuditConfiguration(auditConfigurations.find(v => !v.drugId));
            for (let drug of this.drugs) {
                let drugAuditConfig = auditConfigurations.find(v => v.drugId == drug.packageID && v.drugSource == drug.drugSource);
                if (drugAuditConfig) {
                    drug.orderAuditId = drugAuditConfig.id;
                    drug.minimumMargin = drugAuditConfig.minimumMargin;
                    drug.maximumMargin = drugAuditConfig.maximumMargin;
                    drug.maximumCopay = drugAuditConfig.maximumCopay;
                }
            }
        } else {
            this.orderAuditConfiguration = new OrderAuditConfiguration();
        }
    }

    private resetSelections() {
        this.customTemplateFile = new File([], "");
        this.uploadingTemplate = false;
        this.templateUploadProgress = 0;
        this.templateErrorMessage = '';
        this.selectedDoctor = new Prescriber();
        this.selectedDrug = new Drug();
    }

    addDoctor(val: Prescriber) {
        if (val && val.id) {
            const wasAdded = this.prescribers.find(doc => doc.id == val.id);
            if (!wasAdded)
                this.prescribers.push(val);
        }
    }

    removePrescriber(prescriber: Prescriber) {
        this.prescribers = this.prescribers.filter(doc => doc.id != prescriber.id);
    }

    async addDrug(val: DrugProduct) {
        if (!val || !val.drugId) return;

        const wasAdded = this.drugs.filter(doc => doc.packageID == Number(val.drugId) && doc.drugSource == val.source)[0];
        if (wasAdded) return;

        const dp = new DrugProgram();
        dp.drug = val;

        const url = `api/drug/${val.drugId}/${val.source}/Program`;

        try {
            //Find if selected drug has already been set for autoselect for another program
            const res = await axios.get<Array<DrugProgram>>(url);
            if (res.data?.length) {
                //Get programs for drug and find the one with autoselect
                const dps = res.data.map(dp => new DrugProgram(dp));
                const autoselectProgram = dps.find(dp => dp.autoselect)?.program || null;
                dp.autoselect = false;
                dp.autoselectProgram = autoselectProgram;
            }
        } catch (err) {
            console.error('Error getting Program information for selected drug', {
                err,
                response: err?.response,
            });
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.drugs.push(dp);
        }
    }

    disableAutoselect(dp: DrugProgram): boolean {
        return !!(dp.autoselectProgram?.id && dp.autoselectProgram?.id != this.program.id);
    }

    removeDrug(drug: DrugProgram) {
        let idx = this.drugs.findIndex(dg => dg.packageID == drug.packageID && dg.drugSource == drug.drugSource);
        if (idx != -1) {
            this.drugs.splice(idx, 1);
        }
    }

    getDrugId(drug: DrugProgram) {
        return `${drug.drugSource}-${drug.packageID}`;
    }

    protected hasInventoryLocations = false;

    async loadHasInventoryLocations() {
        try {
            this.hasInventoryLocations = (await axios.get<boolean>("/inventory/has-inventory-locations")).data;
        } catch {
            //ignore
        }
    }

    handleTemplateFileSelection(event: Event) {
        const target = event.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
            const file = target.files[0];
            if (!file.name.toLowerCase().endsWith('.docx')) {
                this.templateErrorMessage = 'Please select a valid .docx file';
                return;
            }
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) {
                this.templateErrorMessage = 'File size must be less than 10MB';
                return;
            }
            this.templateErrorMessage = '';
            this.customTemplateFile = file;
        }
        // Clear file input so same file can be re-selected
        if (this.$refs.templateFileInput) {
            this.$refs.templateFileInput.value = '';
        }
    }

    async downloadCustomTemplate() {
        try {
            const response = await axios.get(`api/program/custom-template/${this.program.id}`, { responseType: 'blob' });
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${this.program.id}${this.program.abbreviation}.docx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error downloading custom template.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    async downloadDefaultTemplate() {
        try {
            const response = await axios.get('api/program/custom-template-default', { responseType: 'blob' });
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'PatEdTemplate.docx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error downloading default template.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    removeCustomTemplate() {
        this.$refs.removeTemplateModal.show();
    }

    async confirmRemoveTemplate() {
        this.removingTemplate = true;
        this.templateErrorMessage = '';
        try {
            const response = await axios.delete<Program>(`api/program/custom-template/${this.program.id}`);
            this.program = new Program(response.data);
            this.customTemplateFile = new File([], "");
            this.$notification(NotificationOptions.success('Custom template removed. The default template will be used.'));
        } catch (err) {
            console.error("Error removing custom template.", err);
            this.templateErrorMessage = 'Failed to remove custom template.';
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.removingTemplate = false;
        }
    }

    copyText(text: string) {
        navigator.clipboard.writeText(text);
        this.$notification(NotificationOptions.success(`Copied token ${text} to clipboard.`));
    }

} // close export of class

</script>


<style scoped>
/*noinspection CssUnusedSymbol*/
.form-check {
    margin-top: 0;
    padding-left: 0;
}

.functionality-flags {
    column-count: 4;
    gap: 8px;
}

.functionality-flags>div {
    margin-bottom: 8px;
}

.feature-flags-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 16px;
}

.feature-group {
    display: flex;
    flex-direction: column;
}

.feature-group strong {
    margin-bottom: 8px;
}

.feature-group>div>div {
    margin-bottom: 8px;
}

.timing-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(525px, 1fr));
    gap: 16px;
}

.contacts-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 24px;
}

.contacts-grid>div {
    display: flex;
    flex-direction: column;
}

.contacts-grid>div>label {
    margin-bottom: 12px;
}

.operations-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 24px;
}

.template-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0;
}
</style>
